import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { STORES_DETAIL_QUERY, YEAR_OVER_YEAR_TRANSACTIONS_QUERY } from '../graphql/queries';
import { useCalendarEvents } from '../hooks/useCalendarEvents';
import { useSelectedStore } from '../hooks/useSelectedStore';
import { gql } from '../lib/api';
import { airRegiBusinessDate, airRegiService, type AirRegiTransactionTime } from '../lib/airRegiBusinessTime';
import { aggregateDrinks, type DrinkTransaction } from '../lib/drinkAnalytics';
import { calculateOccupancyDay, type StayTransaction } from '../lib/stayOccupancy';

type Segment = 'all' | 'lunch' | 'dinner';
type Metric = 'sales' | 'customers' | 'spend' | 'occupancy' | 'stay' | 'alcohol';
type CountBasis = 'customers' | 'payments' | null;

interface Store {
  id: string;
  name: string;
  openDate: string | null;
  airregiBusinessDayStart: string | null;
  lunchStart: string | null;
  lunchEnd: string | null;
  lunchAggregationEnd: string | null;
  dinnerStart: string | null;
  dinnerEnd: string | null;
  dinnerAggregationEnd: string | null;
}

interface Transaction extends AirRegiTransactionTime, StayTransaction, DrinkTransaction {
  total: number;
}

interface DisplayOption { key: string; store: Store; segment: Segment; label: string }
type Values = Record<Metric, number | null>;
interface MonthResult { values: Values; countBasis: CountBasis; excludedEvents: number }
interface ChartPoint {
  month: string;
  current: number | null;
  previous: number | null;
  currentBasis: CountBasis;
  previousBasis: CountBasis;
}

const metrics: { key: Metric; title: string; unit: string; digits: number; note: string }[] = [
  { key: 'sales', title: '売上', unit: '円', digits: 0, note: '会計金額の合計' },
  { key: 'customers', title: '客数', unit: '人', digits: 0, note: '来客数の合計' },
  { key: 'spend', title: '客単価', unit: '円', digits: 0, note: '売上 ÷ 客数' },
  { key: 'occupancy', title: '平均滞在人数', unit: '人', digits: 1, note: '営業中の1時間あたり推定在席人数' },
  { key: 'stay', title: '平均滞在時間', unit: '分', digits: 0, note: '伝票ごとの滞在時間の平均' },
  { key: 'alcohol', title: 'アルコールドリンク杯数（1人あたり）', unit: '杯/人', digits: 2, note: 'アルコール注文数量 ÷ 客数' },
];
const emptyValues = (): Values => ({ sales: null, customers: null, spend: null, occupancy: null, stay: null, alcohol: null });
const jstToday = (): string => new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());
const dateAt = (year: number, month: number, day: number): string =>
  new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
const addDays = (date: string, days: number): string => {
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
};
const monthKey = (year: number, month: number): string => `${year}-${String(month).padStart(2, '0')}`;
const formatValue = (value: number | null, unit: string, digits: number): string =>
  value == null ? '—' : `${value.toLocaleString('ja-JP', { maximumFractionDigits: digits, minimumFractionDigits: digits })}${unit}`;

// 2年分を一度に開く際もAppSyncへの同時リクエスト数を抑える。
let activeRequests = 0;
const pendingRequests: Array<() => void> = [];
const fetchTransactions = async (storeId: string, from: string, to: string): Promise<Transaction[]> => {
  if (activeRequests >= 8) await new Promise<void>((resolve) => pendingRequests.push(resolve));
  activeRequests += 1;
  try {
    const result = await gql<{ transactions: Transaction[] }>(YEAR_OVER_YEAR_TRANSACTIONS_QUERY, { storeId, from, to });
    return result.transactions;
  } finally {
    activeRequests -= 1;
    pendingRequests.shift()?.();
  }
};

// 月単位の取引QueryはDynamoDBの応答上限に達し得るため、7日ずつ取得する。
const fetchMonth = async (storeId: string, year: number, month: number): Promise<Transaction[]> => {
  const from = dateAt(year, month, 1);
  const last = dateAt(year, month + 1, 1); // 深夜の会計が翌月1日に入ることがある。
  const chunks: { from: string; to: string }[] = [];
  for (let start = from; start <= last; start = addDays(start, 7)) {
    const end = addDays(start, 6);
    chunks.push({ from: start, to: end < last ? end : last });
  }
  const responses = await Promise.all(chunks.map((chunk) => fetchTransactions(storeId, chunk.from, chunk.to)));
  return responses.flat();
};

const monthValues = (
  transactions: Transaction[], store: Store, segment: Segment, year: number, month: number,
  today: string, events: Set<string>, priorCutoff: string | null,
): MonthResult => {
  const first = dateAt(year, month, 1);
  const last = dateAt(year, month + 1, 0);
  const end = priorCutoff && priorCutoff < last ? priorCutoff : last < today ? last : today;
  if (first > end || (store.openDate && store.openDate > end)) return { values: emptyValues(), countBasis: null, excludedEvents: 0 };
  let excludedEvents = 0;
  for (let day = first; day <= end; day = addDays(day, 1)) if (events.has(day)) excludedEvents += 1;
  const matching = transactions.filter((transaction) => {
    const date = airRegiBusinessDate(transaction, store.airregiBusinessDayStart);
    return date >= first && date <= end && (!store.openDate || date >= store.openDate)
      && !events.has(date) && (segment === 'all' || airRegiService(transaction, store) === segment);
  });
  if (!matching.length) return { values: emptyValues(), countBasis: null, excludedEvents };

  const sales = matching.reduce((sum, transaction) => sum + (Number(transaction.total) || 0), 0);
  const completeCustomers = matching.every((transaction) => transaction.customerCount != null && transaction.customerCount > 0);
  const countBasis: CountBasis = completeCustomers ? 'customers' : 'payments';
  const count = completeCustomers
    ? matching.reduce((sum, transaction) => sum + Number(transaction.customerCount), 0)
    : matching.length;
  const validStay = matching.filter((transaction) => transaction.stayDurationSec != null && transaction.stayDurationSec > 0);
  const stay = validStay.length / matching.length >= .8
    ? validStay.reduce((sum, transaction) => sum + transaction.stayDurationSec!, 0) / validStay.length / 60 : null;
  const startTime = segment === 'lunch' ? store.lunchStart : store.lunchStart ?? store.dinnerStart;
  const endTime = segment === 'lunch' ? store.lunchEnd : store.dinnerEnd;
  const startHour = startTime ? Number(startTime.split(':')[0]) : 0;
  const endHour = endTime ? Math.ceil(Number(endTime.split(':')[0]) + Number(endTime.split(':')[1]) / 60) : 24;
  const occupied: number[] = [];
  for (let day = first; day <= end; day = addDays(day, 1)) {
    if (events.has(day) || (store.openDate && day < store.openDate)) continue;
    const daily = matching.filter((transaction) => airRegiBusinessDate(transaction, store.airregiBusinessDayStart) === day);
    if (!daily.length) continue;
    const result = calculateOccupancyDay(daily, day, startHour, endHour, null, store.airregiBusinessDayStart ?? '05:00');
    if (result.reliableOccupancy) occupied.push(...result.points.flatMap((point) => point.occupied == null ? [] : [point.occupied]));
  }
  const drinks = aggregateDrinks(matching);
  return { values: {
    sales, customers: count, spend: sales / count,
    occupancy: occupied.length ? occupied.reduce((sum, value) => sum + value, 0) / occupied.length : null,
    stay, alcohol: countBasis === 'customers' ? drinks.alcoholPerGuest : drinks.alcoholCount / count,
  }, countBasis, excludedEvents };
};

const MetricChart = ({ metric, points, year }: { metric: typeof metrics[number]; points: ChartPoint[]; year: number }) => {
  const values = points.flatMap((point) => [point.current, point.previous].filter((value): value is number => value != null));
  const max = Math.max(1, ...values) * 1.12;
  const currentMonths = points.filter((point) => point.current != null).length;
  const previousMonths = points.filter((point) => point.previous != null).length;
  const usesCount = metric.key === 'customers' || metric.key === 'spend' || metric.key === 'alcohol';
  const fallbackMonths = usesCount ? [year - 1, year].flatMap((displayYear) => {
    const affected = points.filter((point) => (displayYear === year ? point.currentBasis : point.previousBasis) === 'payments')
      .map((point) => Number(point.month));
    return affected.length ? [`${displayYear}年${affected.length === 12 ? '全月' : `${affected.join('・')}月`}`] : [];
  }) : [];
  const title = metric.key === 'customers' && fallbackMonths.length ? '客数／決済数'
    : metric.key === 'spend' && fallbackMonths.length ? '客単価／決済単価'
      : metric.key === 'alcohol' && fallbackMonths.length ? 'アルコールドリンク杯数（1人／1決済あたり）'
        : metric.title;
  const note = metric.key === 'customers' ? '決済数を表示しています'
    : metric.key === 'spend' ? '売上 ÷ 決済数で計算しています'
      : metric.key === 'alcohol' ? 'アルコール杯数 ÷ 決済数で計算しています' : '';
  const unitLabel = fallbackMonths.length && metric.key === 'customers' ? '人 / 件'
    : fallbackMonths.length && metric.key === 'alcohol' ? '杯/人・杯/件' : metric.unit;
  return <section className="yoy-panel">
    <header className="yoy-panel-header">
      <div><span className="yoy-panel-index">{String(metrics.findIndex((item) => item.key === metric.key) + 1).padStart(2, '0')} / 06</span><h2>{title}</h2><p>{metric.note}</p></div>
      <span className="yoy-panel-unit">{unitLabel}</span>
    </header>
    <div className="yoy-chart" role="img" aria-label={`${metric.title}の${year}年と${year - 1}年の月別比較`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 12, right: 10, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#e9e3dd" strokeDasharray="2 4" vertical={false} />
          <XAxis dataKey="month" interval={0} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#887f77' }} />
          <YAxis width={62} domain={[0, max]} tickLine={false} axisLine={false} tick={{ fontSize: 9, fill: '#887f77' }}
            tickFormatter={(value: number) => metric.key === 'sales' ? `${Math.round(value / 10000)}万` : Number(value.toFixed(metric.digits)).toLocaleString('ja-JP')} />
          <Tooltip formatter={(value, name, item) => {
            const point = item.payload as ChartPoint;
            const basis = item.dataKey === 'current' ? point.currentBasis : point.previousBasis;
            const unit = basis === 'payments' && metric.key === 'customers' ? '件'
              : basis === 'payments' && metric.key === 'alcohol' ? '杯/件' : metric.unit;
            return [formatValue(value == null ? null : Number(value), unit, metric.digits), `${name}${usesCount && basis === 'payments' ? '（決済数基準）' : ''}`];
          }}
            labelFormatter={(label) => `${label}月`} />
          <Line dataKey="previous" name={`${year - 1}年`} stroke="#bd8882" strokeWidth={2} strokeDasharray="5 4"
            dot={{ r: 2, fill: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={false} />
          <Line dataKey="current" name={`${year}年`} stroke="#0088b0" strokeWidth={2.7}
            dot={{ r: 2.5, fill: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 4 }} connectNulls={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
    {fallbackMonths.length > 0 && <p className="yoy-fallback-note">※ {fallbackMonths.join('、')}は客数情報がないため、{note}。客数がある月は従来どおり来客数を使用しています。</p>}
    {!currentMonths && !previousMonths && <p className="yoy-empty">比較できるデータがありません。</p>}
  </section>;
};

export const YearOverYearPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedStoreId, setStoredStoreId] = useSelectedStore();
  const today = jstToday();
  const currentYear = Number(today.slice(0, 4));
  const requestedYear = Number(searchParams.get('year'));
  const [year, setYear] = useState(requestedYear >= 2001 && requestedYear <= currentYear ? requestedYear : currentYear);
  const [selectedKey, setSelectedKey] = useState(`${searchParams.get('store') ?? storedStoreId}:${searchParams.get('segment') ?? 'all'}`);
  const storesQ = useQuery({ queryKey: ['storesDetail'], queryFn: () => gql<{ stores: Store[] }>(STORES_DETAIL_QUERY) });
  const stores = useMemo(() => storesQ.data?.stores ?? [], [storesQ.data?.stores]);
  const options = useMemo(() => [...stores].sort((a, b) => Number(Boolean(b.lunchStart)) - Number(Boolean(a.lunchStart)))
    .flatMap((store): DisplayOption[] => store.lunchStart && store.lunchEnd ? [
      { key: `${store.id}:lunch`, store, segment: 'lunch', label: `${store.name}（ランチ）` },
      { key: `${store.id}:dinner`, store, segment: 'dinner', label: `${store.name}（ディナー）` },
    ] : [{ key: `${store.id}:all`, store, segment: 'all', label: store.name }]), [stores]);
  const selected = options.find((option) => option.key === selectedKey)
    ?? options.find((option) => option.store.id === selectedKey.split(':')[0]) ?? options[0];
  const storeId = selected?.store.id ?? '';
  const segment = selected?.segment ?? 'all';

  useEffect(() => { if (selected) setStoredStoreId(storeId); }, [selected, setStoredStoreId, storeId]);
  useEffect(() => {
    if (!storeId) return;
    const next = new URLSearchParams(searchParams);
    next.set('store', storeId);
    if (segment === 'all') next.delete('segment'); else next.set('segment', segment);
    next.set('year', String(year));
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [searchParams, segment, setSearchParams, storeId, year]);

  const months = useMemo(() => [year - 1, year].flatMap((y) => Array.from({ length: 12 }, (_, index) => ({ year: y, month: index + 1 })))
    .filter(({ year: y, month }) => monthKey(y, month) <= today.slice(0, 7)), [today, year]);
  const eventsQ = useCalendarEvents(`${year - 1}-01-01`, `${year}-12-31`);
  const monthlyQ = useQueries({ queries: months.map(({ year: y, month }) => ({
    queryKey: ['yearOverYearTransactions', storeId, y, month],
    enabled: Boolean(storeId),
    queryFn: () => fetchMonth(storeId, y, month),
    staleTime: 5 * 60_000,
  })) });
  const monthData = useMemo(() => new Map(months.map(({ year: y, month }, index) =>
    [monthKey(y, month), monthlyQ[index]?.data ?? []])), [months, monthlyQ]);
  const eventDates = useMemo(() => new Set((eventsQ.data?.calendarEvents ?? [])
    .filter((event) => event.storeId === storeId).map((event) => event.date)), [eventsQ.data, storeId]);
  const results = useMemo(() => {
    if (!selected) return new Map<string, MonthResult>();
    const map = new Map<string, MonthResult>();
    for (const { year: y, month } of months) {
      const priorCutoff = y === year - 1 && year === currentYear && month === Number(today.slice(5, 7))
        ? dateAt(y, month, Math.min(Number(today.slice(8, 10)), new Date(Date.UTC(y, month, 0)).getUTCDate())) : null;
      map.set(monthKey(y, month), monthValues(monthData.get(monthKey(y, month)) ?? [], selected.store, segment,
        y, month, today, eventDates, priorCutoff));
    }
    return map;
  }, [currentYear, eventDates, monthData, months, segment, selected, today, year]);
  const chartData = useMemo(() => new Map(metrics.map(({ key }): [Metric, ChartPoint[]] => [key,
    Array.from({ length: 12 }, (_, index) => ({
      month: String(index + 1),
      current: results.get(monthKey(year, index + 1))?.values[key] ?? null,
      previous: results.get(monthKey(year - 1, index + 1))?.values[key] ?? null,
      currentBasis: results.get(monthKey(year, index + 1))?.countBasis ?? null,
      previousBasis: results.get(monthKey(year - 1, index + 1))?.countBasis ?? null,
    }))])), [results, year]);
  const loading = storesQ.isLoading || eventsQ.isLoading || monthlyQ.some((query) => query.isLoading);
  const error = storesQ.isError || eventsQ.isError || monthlyQ.some((query) => query.isError);
  const eventCount = [...results.values()].reduce((sum, result) => sum + result.excludedEvents, 0);

  return <div className="sales-period-page yoy-page">
    <header className="sales-period-heading">
      <div><span className="kpi-weekly-kicker">KPI LAB / YEAR OVER YEAR</span><h1>昨年対比</h1>
        <p>今年と昨年の月別推移を、売上・客数・客単価・平均滞在人数・平均滞在時間・アルコールドリンク杯数で比較します。</p></div>
      <label className="kpi-weekly-store"><span>表示店舗・時間帯</span>
        <select value={selected?.key ?? ''} onChange={(event) => setSelectedKey(event.target.value)}>
          {options.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
        </select>
      </label>
    </header>
    <div className="kpi-weekly-note"><span>KPI検討用</span><p>Airレジの来店営業日で集計し、イベント日は除外します。平均滞在人数は伝票作成から会計までの在席時間を1時間単位で推定します。客数情報がない月の代替計算は各グラフの下に表示します。</p></div>
    <div className="sales-period-controls">
      <label><span>比較する年</span><input type="number" min="2001" max={currentYear} value={year}
        onChange={(event) => { const next = Number(event.target.value); if (next >= 2001 && next <= currentYear) setYear(next); }} /></label>
      <div className="kpi-weekly-range-actions">
        <button type="button" onClick={() => setYear((value) => Math.max(2001, value - 1))}>← 前年</button>
        <button type="button" disabled={year >= currentYear} onClick={() => setYear((value) => Math.min(currentYear, value + 1))}>翌年 →</button>
        <button type="button" onClick={() => setYear(currentYear)}>今年</button>
      </div>
      <p>{year}年 ／ {year - 1}年</p>
    </div>
    <div className="yoy-legend" aria-label="グラフの凡例"><span><i className="yoy-current" />{year}年</span><span><i className="yoy-previous" />{year - 1}年</span></div>
    {year === currentYear && <p className="yoy-note">{today.slice(0, 4)}年{Number(today.slice(5, 7))}月は途中経過です。昨年の同月も{Number(today.slice(8, 10))}日までで比較します。</p>}
    {eventCount > 0 && <p className="yoy-note">イベント日を除外: 延べ{eventCount}日（両年合計）</p>}
    {error && <section className="kpi-weekly-state" role="alert">比較データを取得できませんでした。再読み込みしてお試しください。</section>}
    {!error && loading && <section className="kpi-weekly-state">月別データを読み込んでいます…</section>}
    {!error && !loading && <div className="yoy-panels">{metrics.map((metric) =>
      <MetricChart key={metric.key} metric={metric} points={chartData.get(metric.key) ?? []} year={year} />)}</div>}
  </div>;
};
